import { useCallback, useEffect, useRef, useState } from 'react';
import {
  posRestigoService,
  type RestigoSessionPushView,
} from '../../../../services/pos-restigo/pos-restigo.service';

type Params = {
  tenantId: string;
  employeeSession: string | null;
  opened: boolean;
};

// Loads recent Restigo session pushes for the POS manager panel and drives manager resend.
export function useRestigoPushesController({ tenantId, employeeSession, opened }: Params) {
  const [rows, setRows] = useState<RestigoSessionPushView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendingSessionId, setResendingSessionId] = useState<number | null>(null);
  const resendLockRef = useRef(false);

  const load = useCallback(async () => {
    if (!employeeSession) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const page = await posRestigoService.listSessionPushes(tenantId, employeeSession);
      setRows(page.rows);
    } catch (loadError: unknown) {
      setError(loadError instanceof Error ? loadError.message : 'טעינת שליחות רסטיגו נכשלה');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, employeeSession]);

  useEffect(() => {
    if (opened) {
      void load();
    }
  }, [opened, load]);

  const resend = useCallback(
    async (sessionId: number) => {
      if (resendLockRef.current || !employeeSession) {
        return;
      }
      resendLockRef.current = true;
      setResendingSessionId(sessionId);
      setError(null);
      try {
        await posRestigoService.resend(tenantId, employeeSession, sessionId);
        await load();
      } catch (resendError: unknown) {
        setError(resendError instanceof Error ? resendError.message : 'שליחה חוזרת לרסטיגו נכשלה');
      } finally {
        resendLockRef.current = false;
        setResendingSessionId(null);
      }
    },
    [tenantId, employeeSession, load],
  );

  return { rows, isLoading, error, resend, resendingSessionId };
}
